import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { filter, take } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class ApiCacheService {
    private dataCache = new Map<string, any>();
    private dataSubjects = new Map<string, BehaviorSubject<any>>();

    /**
     * Patterns passed to clearCache() whose CacheStorage entries are still being
     * deleted. The delete is asynchronous and clearCache() cannot await it —
     * every caller invalidates and re-requests in the same tick — so a getData()
     * that follows would hit loadCachedData() and replay the very copy that was
     * just invalidated. While a pattern sits here, matching URLs skip the browser
     * cache and wait for the network; the pattern is dropped as soon as a fresh
     * response for a matching URL lands.
     */
    private invalidatedPatterns = new Set<string>();

    /**
     * Many of these payloads embed CloudFront signed image URLs with a short
     * `Expires` window. getData() used to fetch a URL exactly once per tab
     * session and replay that same response forever, so a gallery left open
     * past the signature's TTL would serve pre-expired URLs and every image
     * would 403 until something else forced a refetch. This TTL makes
     * getData() refresh stale entries in the background on next access.
     */
    private readonly DATA_TTL_MS = 2 * 60 * 1000; // 2 minutes
    private fetchTimestamps = new Map<string, number>();

    constructor(private http: HttpClient) {
        // Listen for SW API update events
        document.addEventListener('api-updated', (event: any) => {
            const url = event.detail;
            this.fetchAndUpdate(url);
        });
    }
    getData<T>(url: string, options?: { headers?: HttpHeaders }): Observable<T> {
        if (!this.dataSubjects.has(url)) {
            this.dataSubjects.set(url, new BehaviorSubject<T | null>(null));
            if (!this.isInvalidated(url)) {
                this.loadCachedData<T>(url);
            }
            this.fetchAndUpdate<T>(url, options?.headers);
        } else if (this.isStale(url)) {
            this.fetchAndUpdate<T>(url, options?.headers);
        }
        return this.dataSubjects.get(url)!.asObservable();
    }

    private isStale(url: string): boolean {
        const fetchedAt = this.fetchTimestamps.get(url);
        return fetchedAt === undefined || Date.now() - fetchedAt > this.DATA_TTL_MS;
    }

    /**
     * Force fetch fresh data from API, bypassing cache
     */
    getFreshData<T>(url: string, options?: { headers?: HttpHeaders }): Observable<T> {
        // Clear existing cache for this URL
        this.clearCache(url);

        // Create new subject and fetch fresh data
        const subject = new BehaviorSubject<T | null>(null);
        this.dataSubjects.set(url, subject);

        this.http.get<T>(url, { headers: options?.headers }).subscribe({
            next: (data) => {
                this.dataCache.set(url, data);
                this.fetchTimestamps.set(url, Date.now());
                subject.next(data);
            },
            error: (err) => {
                subject.error(err);
            }
        });

        // Return only the first non-null value
        return subject.asObservable().pipe(
            filter((data): data is T => data !== null),
            take(1)
        );
    }

    /**
     * Clear cache for a specific URL pattern
     */
    clearCache(urlPattern: string): void {
        this.invalidatedPatterns.add(urlPattern);

        // Both maps, not just dataCache: a request that failed — or that is still
        // in flight — leaves a subject parked on its null seed with no dataCache
        // entry at all, and iterating dataCache alone would leave that dead
        // subject in place for the rest of the tab session. Every later getData()
        // would then re-subscribe to it and receive nothing but the seed.
        for (const key of [...this.dataCache.keys(), ...this.dataSubjects.keys(), ...this.fetchTimestamps.keys()]) {
            if (key.includes(urlPattern)) {
                this.dataCache.delete(key);
                this.dataSubjects.delete(key);
                this.fetchTimestamps.delete(key);
            }
        }

        // Clear from browser cache
        if ('caches' in window) {
            caches.open('api-cache').then(cache => {
                cache.keys().then(keys => {
                    keys.forEach(request => {
                        if (request.url.includes(urlPattern)) {
                            cache.delete(request);
                        }
                    });
                });
            });
        }
    }

    /**
     * Clear all cache for media count API
     */
    clearMediaCountCache(): void {
        this.clearCache('pwa_get_media_count');
    }

    private isInvalidated(url: string): boolean {
        for (const pattern of this.invalidatedPatterns) {
            if (url.includes(pattern)) return true;
        }
        return false;
    }

    private fetchAndUpdate<T>(url: string, headers?: HttpHeaders) {
        this.http.get<T>(url, { headers }).subscribe({
            next: (data) => {
                if (data == null) return;
                this.dataCache.set(url, data);
                this.fetchTimestamps.set(url, Date.now());
                // Fresh data is here, so the browser copy is no longer the stale
                // one clearCache() was racing — let it be read again.
                this.invalidatedPatterns.forEach(pattern => {
                    if (url.includes(pattern)) this.invalidatedPatterns.delete(pattern);
                });
                this.dataSubjects.get(url)?.next(data);
            },
            error: () => {}
        });
    }

    private async loadCachedData<T>(url: string) {
        if ('caches' in window) {
            const cache = await caches.open('api-cache');
            const response = await cache.match(url);
            if (response) {
                const data = await response.json();
                this.dataCache.set(url, data);
                this.dataSubjects.get(url)?.next(data);
            }
        }
    }


}
